import { ICartItem } from "@/pages/point-of-sale/models/cart-item.model.ts";
import { safeMultiply } from "@/utilities/decimal.utils";
import { findContainerTypeInProduct } from "@/pages/point-of-sale/utilities/container-type-resolve.utils";

/** Modo de ajuste de precio en una línea de venta */
export type LinePriceMode = "none" | "unit_edit" | "line_discount" | "points";

export function getCatalogueUnitPrice(item: ICartItem): number {
  const fromProduct =
    findContainerTypeInProduct(item.product, {
      id: item.containerType.id,
      name: item.containerType.name,
      alias: item.containerType.alias,
    })?.price ?? item.containerType.price;
  return fromProduct;
}

export function getListLineTotal(quantity: number, catalogueUnitPrice: number): number {
  return safeMultiply(quantity, catalogueUnitPrice);
}

/** Inferir modo para ítems persistidos antes del cambio */
export function resolveLinePriceMode(item: ICartItem): LinePriceMode {
  if ((item.amountRedeemed ?? 0) > 0 || (item.pointsUsed ?? 0) > 0) {
    return "points";
  }
  if (item.linePriceMode && item.linePriceMode !== "none") {
    return item.linePriceMode;
  }
  if (
    item.discountType === "discount_by_percentage" ||
    item.discountType === "discount_by_amount"
  ) {
    const list = getCatalogueUnitPrice(item);
    const listTotal = getListLineTotal(item.totalQuantity, list);
    const charged = item.totalPrice + (item.amountRedeemed ?? 0);
    if (listTotal - charged > 0.001) return "line_discount";
  }
  if (item.discountType && item.discountedAmount > 0) {
    return "unit_edit";
  }
  const list = getCatalogueUnitPrice(item);
  const chargedBase =
    item.totalQuantity > 0
      ? (item.totalPrice + (item.amountRedeemed ?? 0)) / item.totalQuantity
      : list;
  if (Math.abs(chargedBase - list) > 0.001) {
    return "unit_edit";
  }
  return "none";
}

export function getEditedUnitPrice(item: ICartItem): number | null {
  if (resolveLinePriceMode(item) !== "unit_edit") return null;
  if (item.editedUnitPrice != null && item.editedUnitPrice > 0) {
    return item.editedUnitPrice;
  }
  const list = getCatalogueUnitPrice(item);
  switch (item.discountType) {
    case "discount_by_price":
      return item.discountedAmount;
    case "discount_by_percentage":
      return list - (item.discountedAmount / 100) * list;
    case "discount_by_amount":
      return list - item.discountedAmount;
    default: {
      if (item.totalQuantity <= 0) return null;
      return (item.totalPrice + (item.amountRedeemed ?? 0)) / item.totalQuantity;
    }
  }
}

/** P/U mostrado al usuario */
export function getDisplayUnitPrice(item: ICartItem): number {
  const list = getCatalogueUnitPrice(item);
  if (resolveLinePriceMode(item) === "line_discount") return list;
  const edited = getEditedUnitPrice(item);
  if (edited != null) return edited;
  return list;
}

/** Importe de lista de la línea (referencia) */
export function getListLineTotalForItem(item: ICartItem): number {
  return getListLineTotal(item.totalQuantity, getCatalogueUnitPrice(item));
}

/** Descuento comercial explícito (solo modo descuento por producto) */
export function getLineProductDiscountAmount(item: ICartItem): number {
  if (resolveLinePriceMode(item) !== "line_discount") return 0;
  const list = getListLineTotalForItem(item);
  const charged = item.totalPrice + (item.amountRedeemed ?? 0);
  return Math.max(0, Number((list - charged).toFixed(2)));
}

export function buildLinePricingPatch(params: {
  catalogueUnitPrice: number;
  quantity: number;
  amountRedeemed: number;
  linePriceMode: LinePriceMode;
  editedUnitPrice?: number;
  agreedLineTotal?: number;
  /** discount_by_percentage | discount_by_amount (valor ingresado en el popup) */
  lineDiscountApiType?: "discount_by_percentage" | "discount_by_amount";
  lineDiscountInput?: number;
}): Pick<
  ICartItem,
  | "linePriceMode"
  | "editedUnitPrice"
  | "discountType"
  | "discountedAmount"
  | "totalPrice"
> {
  const {
    catalogueUnitPrice,
    quantity,
    amountRedeemed,
    linePriceMode,
    editedUnitPrice,
    agreedLineTotal,
    lineDiscountApiType,
    lineDiscountInput,
  } = params;
  const listTotal = getListLineTotal(quantity, catalogueUnitPrice);

  if (linePriceMode === "unit_edit" && editedUnitPrice != null) {
    const totalBeforePoints = safeMultiply(quantity, editedUnitPrice);
    return {
      linePriceMode: "unit_edit",
      editedUnitPrice,
      discountType: "",
      discountedAmount: 0,
      totalPrice: Math.max(0, Number((totalBeforePoints - amountRedeemed).toFixed(2))),
    };
  }

  if (linePriceMode === "line_discount" && agreedLineTotal != null) {
    const agreed = Math.min(listTotal, Math.max(0, agreedLineTotal));
    const discountAmount = Number((listTotal - agreed).toFixed(2));
    const apiType = lineDiscountApiType ?? "discount_by_amount";
    const discountedAmount =
      lineDiscountInput != null && lineDiscountInput >= 0
        ? lineDiscountInput
        : apiType === "discount_by_percentage"
        ? listTotal > 0
          ? Number(((discountAmount / listTotal) * 100).toFixed(2))
          : 0
        : discountAmount;
    return {
      linePriceMode: "line_discount",
      editedUnitPrice: undefined,
      discountType: apiType,
      discountedAmount,
      totalPrice: Math.max(0, Number((agreed - amountRedeemed).toFixed(2))),
    };
  }

  return {
    linePriceMode: "none",
    editedUnitPrice: undefined,
    discountType: "",
    discountedAmount: 0,
    totalPrice: Math.max(0, Number((listTotal - amountRedeemed).toFixed(2))),
  };
}
